import React from 'react';
import { Plane, Clock, Book, UserCheck } from 'lucide-react';

/**
 * Компонент боковой панели с информацией о полете и авиакомпаниях России
 */
const FlightInfoSidebar = ({ formData, results }) => {
  // Скорость выбранного типа самолета
  const getAircraftSpeed = () => {
    if (results && results.aircraftSpeed) {
      return results.aircraftSpeed;
    }

    switch (formData.aircraftType) {
      case 'regional': return 550;
      case 'small': return 800;
      case 'medium': return 850;
      case 'large': return 900;
      default: return 850;
    }
  };

  // Информация о классах обслуживания
  const getClassInfo = () => {
    switch (formData.flightClass) {
      case 'economy':
        return {
          name: 'Эконом-класс',
          description: 'Базовый уровень комфорта, включает стандартное обслуживание, питание и размещение.',
          icon: <UserCheck className="w-5 h-5 text-blue-500" />
        };
      case 'comfort':
        return {
          name: 'Комфорт-класс',
          description: 'Улучшенный эконом-класс с дополнительным пространством для ног, расширенным меню и приоритетной посадкой.',
          icon: <UserCheck className="w-5 h-5 text-green-500" />
        };
      case 'business':
        return {
          name: 'Бизнес-класс',
          description: 'Повышенный комфорт с раскладывающимися креслами, премиальным обслуживанием и доступом в бизнес-залы.',
          icon: <UserCheck className="w-5 h-5 text-purple-500" />
        };
      case 'first':
        return {
          name: 'Первый класс',
          description: 'Высший уровень комфорта с индивидуальными каютами, эксклюзивным обслуживанием и персональным ассистентом.',
          icon: <UserCheck className="w-5 h-5 text-amber-500" />
        };
      default:
        return {
          name: 'Эконом-класс',
          description: 'Базовый уровень комфорта.',
          icon: <UserCheck className="w-5 h-5 text-blue-500" />
        };
    }
  };

  const classInfo = getClassInfo();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Plane className="w-5 h-5 mr-2 text-blue-500" />
        Информация о полетах
      </h3>

      {/* Информация о выбранном типе самолета */}
      <div className="p-4 mb-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
        <h4 className="text-base font-medium text-gray-900 dark:text-white mb-2 flex items-center">
          <Plane className="w-4 h-4 mr-2 text-blue-500" />
          Характеристики самолета
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Ваш маршрут рассчитан для типа самолета:
        </p>
        <div className="font-medium text-blue-700 dark:text-blue-300 mb-1">
          {results ? results.aircraftName : formData.aircraftType === 'regional' ? 'Региональный самолет' : 
           formData.aircraftType === 'small' ? 'Малый самолет' : 
           formData.aircraftType === 'medium' ? 'Средний самолет' : 'Дальнемагистральный самолет'}
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Средняя крейсерская скорость: <span className="font-medium">{getAircraftSpeed()} км/ч</span>
        </div>
      </div>

      {/* Информация о классе обслуживания */}
      <div className="p-4 mb-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="text-base font-medium text-gray-900 dark:text-white mb-2 flex items-center">
          {classInfo.icon}
          <span className="ml-2">{classInfo.name}</span>
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {classInfo.description}
        </p>
      </div>

      {/* Информация об основных авиакомпаниях России */}
      <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="text-base font-medium text-gray-900 dark:text-white mb-3 flex items-center">
          <Book className="w-4 h-4 mr-2 text-blue-500" />
          Крупнейшие авиакомпании России
        </h4>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
          <li className="flex items-start">
            <span className="w-3 h-3 rounded-full bg-blue-500 mt-1 mr-2 flex-shrink-0"></span>
            <div>
              <span className="font-medium">Аэрофлот</span> - национальный авиаперевозчик России с обширной сетью маршрутов по России и миру
            </div>
          </li>
          <li className="flex items-start">
            <span className="w-3 h-3 rounded-full bg-red-500 mt-1 mr-2 flex-shrink-0"></span>
            <div>
              <span className="font-medium">S7 Airlines</span> - второй по величине авиаперевозчик России с хабами в Москве и Новосибирске
            </div>
          </li>
          <li className="flex items-start">
            <span className="w-3 h-3 rounded-full bg-green-500 mt-1 mr-2 flex-shrink-0"></span>
            <div>
              <span className="font-medium">Уральские авиалинии</span> - крупный авиаперевозчик с базой в Екатеринбурге
            </div>
          </li>
          <li className="flex items-start">
            <span className="w-3 h-3 rounded-full bg-purple-500 mt-1 mr-2 flex-shrink-0"></span>
            <div>
              <span className="font-medium">Победа</span> - лоукостер с фокусом на внутренние и популярные международные направления
            </div>
          </li>
          <li className="flex items-start">
            <span className="w-3 h-3 rounded-full bg-amber-500 mt-1 mr-2 flex-shrink-0"></span>
            <div>
              <span className="font-medium">UTair</span> - один из крупнейших вертолетных операторов мира и пассажирский авиаперевозчик
            </div>
          </li>
        </ul>
      </div>

      {/* Интересные факты о полетах */}
      <div className="mt-6">
        <h4 className="text-base font-medium text-gray-900 dark:text-white mb-3 flex items-center">
          <Clock className="w-4 h-4 mr-2 text-blue-500" />
          Интересные факты
        </h4>
        <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
          <p>
            Самый длинный беспосадочный рейс в России осуществляется между Москвой и Владивостоком - 
            около 9 часов полета и более 6,400 км.
          </p>
          <p>
            В российском небе ежедневно выполняется более 3,000 рейсов, 
            обеспечивая транспортное сообщение для территории, охватывающей 11 часовых поясов.
          </p>
          <p>
            Самолеты Sukhoi Superjet 100 и МС-21 являются основой программы импортозамещения в гражданской авиации России.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FlightInfoSidebar;